import { useFocusEffect } from "@react-navigation/core";
import { NativeStackScreenProps } from "@react-navigation/native-stack";

import { ButtonV2, Tooltip } from "@kolmeo/ui-components";
import { Box, Text, useTheme } from "@kolmeo/ui-core";

import { Seperator, TooltipBranded } from "@src/components";
import { PageContainer, ReactQueryContainer } from "@src/containers";
import { useCurrentUser, useMediaQuery } from "@src/hooks";
import { useSetPreviousScreenName } from "@src/hooks/useSetPreviousScreenName/useSetPreviousScreenName";
import { PaymentSettingsStackParamsList } from "@src/navigation/PaymentSettingsNavigator/PaymentSettingsNavigator";
import { useBranding } from "@src/navigation/useBranding";
import { TenancyPaymentDetails } from "@src/navigation/TenancyNavigator/types";
import { useCustomNavigation } from "@src/navigation/hooks/useCustomNavigation";
import { useGlobalTenant } from "@src/navigation/hooks/useGlobalTenant";
import { usePreviousScreenName } from "@src/store";
import { PaymentMethod, OccupancyStatus } from "@src/types";
import { BankAccountDetails, BankAccountDetailsResponse, DeactivateBankAccountData } from "@src/types/PaymentPreferences";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import ActionRequiredModal from "../../../components/Modal/variants/ActionRequiredModal";
import ConfirmationModal from "../../../components/Modal/variants/AlertModal";
import { useGetAllTenancies, useGetTenancyDetails } from "../TenancyOverview/api/useTenancyOverview";
import { useDisableFeatures } from "../hooks/useDisableFeatures";
import { usePaymentMethodLabel } from "../hooks/usePaymentMethodLabel";
import { useDeactivateBankAccountMutation, useGetAllBankAccounts } from "../hooks/usePaymentPreferences";
import { useTenancyOccupancyStatus } from "../hooks/useTenancyOccupancyStatus";
import BankDetailsList from "./components/Lists/BankDetailsList";
import BPayDetailsList from "./components/Lists/BPayDetailsList";
import VirtualAccountDetailsList from "./components/Lists/VirtualAccountDetailsList";
import PaymentMethodModal from "./components/Modals/PaymentMethodModal/PaymentMethodModal";

const PaymentDetails = ({ navigation }: NativeStackScreenProps<PaymentSettingsStackParamsList, "Details">) => {
  const { t } = useTranslation(["Common", "Tenant"]);
  const { goBack } = useCustomNavigation();
  const {
    tenancyId_InLocalStorage: tenancyId,
    getSelectedTenancy_InLocalStorage: getSelectedTenancy,
    handleSetTenancyData,
    redirectId,
    handleClearRedirectId
  } = useGlobalTenant();
  const { brandingObject } = useBranding();
  const { data, isError, isLoading, error, refetch } = useGetAllBankAccounts(tenancyId);
  const { data: tenancyData } = useGetTenancyDetails(tenancyId);
  const { mutateAsync: deactivateBankAccountAsync } = useDeactivateBankAccountMutation();
  const isTenancyOccupancyStatus = useTenancyOccupancyStatus(tenancyData?.occupancyStatus);

  const { disableDeactivation, disableSetDefault, disableAddBankAccounts, disableEditNickname, hideCta, hidePaymentMethodList } =
    useDisableFeatures(isTenancyOccupancyStatus, tenancyData?.paymentMethodCode);

  const { isBelowMobile, isBelowTablet } = useMediaQuery();
  const handleBackLink = () => goBack();
  const { palette, spacing } = useTheme();
  // modal controls
  const [showPaymentMethodDetails, setShowPaymentMethodDetails] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showCompletedModal, setShowCompletedModal] = useState(false);
  const [showCannotRemoveModalDueToRestriction, setShowCannotRemoveModalDueToRestriction] = useState(false);
  const [showCannotRemoveDueToDefaultModal, setShowCannotRemoveDueToDefaultModal] = useState(false);

  const [selectedDetails, setSelectedDetails] = useState<BankAccountDetailsResponse>();
  const [isViewInvoice, setIsViewInvoice] = useState(false);
  const [selectedPaymentDetails, setSelectedPaymentDetails] = useState<TenancyPaymentDetails>();
  // const getAllTenancyRequests = useGetAllTenancies();
  // const { currentUser } = useCurrentUser();

  const previousScreenName = usePreviousScreenName();
  const { setPreviousScreenNameToStore } = useSetPreviousScreenName();

  useEffect(() => {
    setPreviousScreenNameToStore(["PaymentDetails", "AddPaymentMethod", "Details"]);
  }, []);

  useFocusEffect(() => {
    // if the user has been redirected to this page from the add payment methods screen, open the modal with the new payment method data.
    const openAddedPaymentMethod = () => {
      if (redirectId !== undefined) {
        refetch().then((res) => {
          const newPaymentDetails = res.data?.bankAccountDetails?.find((pd) => pd.accountId == redirectId);
          setShowPaymentMethodDetails(true);
          setSelectedDetails(newPaymentDetails);
          handleClearRedirectId();
        });
      }
    };
    openAddedPaymentMethod();
  });

  useEffect(() => {
    if (tenancyId) navigation?.setParams({ tenancyId });
  }, [tenancyId]);

  useEffect(() => {
    if (selectedDetails || selectedPaymentDetails) {
      setShowPaymentMethodDetails(true);
    }
  }, [selectedDetails, selectedPaymentDetails]);

  const paymentMethodLabel = usePaymentMethodLabel(tenancyData?.paymentMethodCode);

  const onPaymentMethodPress = (item: BankAccountDetailsResponse) => {
    setSelectedDetails(item);
    if (isViewInvoice) {
      setIsViewInvoice(false);
    }
  };
  const onViewInvoice = (item: BankAccountDetailsResponse) => {
    onPaymentMethodPress(item);
    setIsViewInvoice(true);
  };
  const onBpayMethodPress = (item: TenancyPaymentDetails) => {
    setSelectedPaymentDetails(item);
  };

  const onRemoveAccountPress = () => {
    if (disableRemoveAccountConfirmation()) {
      if (tenancyData?.paymentMethodCode === PaymentMethod.directDebit && selectedDetails?.isDefault) {
        setShowCannotRemoveModalDueToRestriction(true);
      } else {
        setShowCannotRemoveDueToDefaultModal(true);
      }
    } else {
      setShowConfirmDelete(true);
    }
  };

  const onConfirmDeletePress = () => {
    setShowConfirmDelete(false);
    // call api, then
    if (selectedDetails?.accountId && tenancyId) {
      const form: DeactivateBankAccountData = {
        accountId: selectedDetails?.accountId,
        tenancyId: tenancyId,
        isDefault: selectedDetails.isDefault
      };
      deactivateBankAccountAsync(form).then((result) => {
        if (result.isSuccessful == true) {
          refetch().finally(() => {
            setShowCompletedModal(true);
            handleTogglePaymentDetailsModal(false);
          });
        } else {
          setShowCannotRemoveModalDueToRestriction(true);
        }
      });
    } else {
      handleTogglePaymentDetailsModal(false);
    }
  };

  const handleTogglePaymentDetailsModal = (toggleShow: boolean) => {
    setShowPaymentMethodDetails(toggleShow);
    setSelectedDetails(undefined);
    setSelectedPaymentDetails(undefined);
  };

  const disableRemoveAccountConfirmation = () => {
    return (
      data?.bankAccountDetails?.length == 1 || (tenancyData?.paymentMethodCode === PaymentMethod.directDebit && selectedDetails?.isDefault)
    );
  };

  const renderEmptyStateTitle = () => {
    if (tenancyData?.paymentMethodCode == undefined) {
      return t("Tenant:PaymentMethods.NoPaymentMethodTitle");
    }
    if (tenancyData?.paymentMethodCode == PaymentMethod.bpay) {
      return t("Tenant:PaymentMethods.BPAYPaymentMethodEmpty");
    } else {
      return t("Tenant:PaymentMethods.PaymentDetailsEmpty");
    }
  };
  const renderEmptyStateContent = () => {
    if (tenancyData?.paymentMethodCode == undefined) {
      return t("Tenant:PaymentMethods.NoPaymentMethodDescription");
    } else if (tenancyData.paymentMethodCode == PaymentMethod.bpay) {
      return t("Tenant:PaymentMethods.BPAYPaymentMethodEmptyDescription");
    }
  };

  const renderEmptyState = () => {
    if (data?.hasDebitNowInvoice) return false;
    switch (tenancyData?.paymentMethodCode) {
      case PaymentMethod.directDebit || PaymentMethod.debitNow:
        return !data || data?.bankAccountDetails?.length == 0;
      case PaymentMethod.bpay || PaymentMethod.virtualAccount:
        return tenancyData.tenancyPaymentDetails == null;
      case null:
        return true;
      default: {
        return false;
      }
    }
  };

  const formatAccountName = (name: string | undefined): string | undefined => {
    if (name) {
      name = name.replace(/[^a-zA-Z0-9 ]/g, "");
      name = "Rent " + name;
      return name.length > 20 ? name.substring(0, 20) : name;
    } else return undefined;
  };
  const getFilteredBankDetailsData = () => {
    const debitNowAccounts = data?.bankAccountDetails?.filter((account) => account.tags?.includes("debit_now"));
    const filteredData: BankAccountDetails | undefined = debitNowAccounts?.length
      ? {
          bankAccountDetails: debitNowAccounts,
          hasDebitNowInvoice: data?.hasDebitNowInvoice ?? false
        }
      : undefined;
    return (
      <>
        <BankDetailsList
          data={filteredData || undefined}
          isBelowMobile={isBelowMobile}
          disableAddBankAccounts={disableAddBankAccounts}
          onListCardPress={onViewInvoice}
          onAddPaymentPress={() => navigation?.navigate("AddPaymentMethod", { from: "Invoice" })}
          paymentMethodLabel={"Invoices"}
          isAddBankButton={true}
          isInvoices={true}
        />
      </>
    );
  };

  return (
    <PageContainer
      isScrollable={true}
      brandingObject={brandingObject}
      headingText={t("PaymentMethods.PaymentSettingsHeader", { ns: "Tenant" })}
      navigateBack={handleBackLink}
      iconType={"cash-multiple"}
      navigateBackLabel={previousScreenName ?? "Back"}
    >
      <>
        <Box>
          <Text color={palette.text.secondary} marginBottom={20}>
            {getSelectedTenancy?.propertyName}
          </Text>
        </Box>
      </>
      <ReactQueryContainer
        isLoading={isLoading}
        isError={isError}
        error={error}
        showEmptyState={renderEmptyState()}
        emptyStateContent={{
          size: "md",
          title: renderEmptyStateTitle(),
          content: renderEmptyStateContent(),
          textColor: brandingObject?.primaryColor,
          setMaxHeight: true,
          callToAction: !disableAddBankAccounts ? (
            <ButtonV2
              testID="button-add-bankaccount-emptystate"
              fullwidth={isBelowMobile}
              variant="primary"
              overrideVariant={{ bgColor: brandingObject?.primaryColor }}
              iconType="plus"
              label={t("Tenant:PaymentMethods.CTAAddNew")}
              onPress={() => navigation?.navigate("AddPaymentMethod", {})}
            />
          ) : undefined
        }}
      >
        {!hidePaymentMethodList ? (
          (() => {
            switch (tenancyData?.paymentMethodCode) {
              case PaymentMethod.directDebit:
              case PaymentMethod.debitNow:
                return (
                  <BankDetailsList
                    data={data}
                    isBelowMobile={isBelowMobile}
                    disableAddBankAccounts={disableAddBankAccounts}
                    onListCardPress={onPaymentMethodPress}
                    onAddPaymentPress={() => navigation?.navigate("AddPaymentMethod", {})}
                    paymentMethodLabel={paymentMethodLabel}
                    isAddBankButton={
                      (PaymentMethod.debitNow && data?.hasDebitNowInvoice) ||
                      (!data?.bankAccountDetails?.length && !data?.hasDebitNowInvoice)
                    }
                    isHeader={PaymentMethod.debitNow && data?.hasDebitNowInvoice && paymentMethodLabel !== "Direct Debit"}
                  />
                );
              case PaymentMethod.bpay:
                return (
                  <BPayDetailsList
                    isBelowMobile={isBelowMobile}
                    onListCardPress={onBpayMethodPress}
                    data={tenancyData?.tenancyPaymentDetails && [tenancyData?.tenancyPaymentDetails]}
                    paymentMethodLabel={paymentMethodLabel}
                  />
                );
              case PaymentMethod.virtualAccount:
                return (
                  <VirtualAccountDetailsList
                    onListCardPress={onBpayMethodPress}
                    isBelowMobile={isBelowMobile}
                    data={tenancyData?.tenancyPaymentDetails && [tenancyData?.tenancyPaymentDetails]}
                    propertyName={formatAccountName(getSelectedTenancy?.propertyName)}
                    paymentMethodLabel={paymentMethodLabel}
                  />
                );
              default:
                return (
                  <>
                    <Box flexDirection={"row"} alignItems={"center"}>
                      <>
                        <Text lineHeight={20} color={palette.text.secondary}>
                          {t("Tenant:PaymentMethods.RentPaymentMethod")}:{" "}
                          <Text fontWeight={600} color={"#000"}>
                            {t("Tenant:PaymentMethods.NoPaymentMethod")}
                          </Text>
                        </Text>
                        {getSelectedTenancy?.status !== OccupancyStatus.inactive && (
                          <Box style={{ marginLeft: 4, alignItems: "center" }}>
                            <TooltipBranded content={t("Tenant:PaymentMethods.ChangePaymentMethodMessage")} position={"bottom"} />
                          </Box>
                        )}
                      </>
                    </Box>
                  </>
                );
            }
          })()
        ) : (
          <></>
        )}

        {data?.hasDebitNowInvoice && paymentMethodLabel !== "Debit Now" && getFilteredBankDetailsData()}
      </ReactQueryContainer>
      {/* Payment Method Modal */}
      <PaymentMethodModal
        hideCTA={hideCta}
        isVisible={showPaymentMethodDetails}
        toggleVisibility={(toggleShow) => handleTogglePaymentDetailsModal(toggleShow)}
        item={selectedDetails}
        detailsItem={selectedPaymentDetails}
        paymentMethod={tenancyData?.paymentMethodCode}
        propertyName={formatAccountName(getSelectedTenancy?.propertyName)}
        handleRefetch={() =>
          refetch().then((res) => {
            const updatedSelected = res?.data?.bankAccountDetails?.find((r) => selectedDetails?.accountId == r.accountId);
            setSelectedDetails(updatedSelected);
          })
        }
        isReloading={isLoading}
        onDeleteAccountPress={onRemoveAccountPress}
        disableDeactivation={disableDeactivation}
        disableSetDefault={disableSetDefault}
        disableEditNickname={disableEditNickname}
        hideSetDefault={isViewInvoice}
      />
      {/* confirmation */}
      <ActionRequiredModal
        heading={t("Common:Generic.IrreversibleAction")}
        iconType={"danger"}
        actionType={"danger"}
        content={t("Tenant:PaymentMethods.DeleteConfirmation")}
        isVisible={showConfirmDelete}
        toggleVisibility={() => setShowConfirmDelete(false)}
        rightText={t("Common:Generic.Remove")}
        onRightButtonPress={onConfirmDeletePress}
      />
      {/* Completed */}
      <ConfirmationModal
        heading={t("Common:Generic.Thanks")}
        iconType={"success"}
        content={t("Tenant:PaymentMethods.DeleteConfirmationText")}
        isVisible={showCompletedModal}
        toggleVisibility={() => {
          refetch();
          setShowCompletedModal(false);
          handleTogglePaymentDetailsModal(false);
        }}
        brandingObject={brandingObject}
      />
      {/* Cannot remove account - display if api check returns false after clicking the remove account button*/}
      <ConfirmationModal
        heading={t("Tenant:PaymentMethods.HeadingCannotRemoveAccount")}
        iconType={"danger"}
        isVisible={showCannotRemoveModalDueToRestriction}
        content={t("Tenant:PaymentMethods.CannotRemoveAccountDescription")}
        toggleVisibility={() => {
          setShowCannotRemoveModalDueToRestriction(false);
        }}
        brandingObject={brandingObject}
      />
      {/* Cannot remove account due to default - display this when a user needs to add an additional account*/}
      <ConfirmationModal
        heading={t("Tenant:PaymentMethods.HeadingCannotRemoveAccount")}
        iconType={"danger"}
        isVisible={showCannotRemoveDueToDefaultModal}
        content={t("Tenant:PaymentMethods.AddAnotherBankAccount")}
        toggleVisibility={() => {
          setShowCannotRemoveDueToDefaultModal(false);
        }}
        brandingObject={brandingObject}
      />
    </PageContainer>
  );
};

export default PaymentDetails;
